import * as auth from './auth';

const API_URL = process.env.REACT_APP_API_URL || '/api';
console.log('Frontend configurado para conectar a:', API_URL);

// Obtiene los encabezados necesarios para las solicitudes HTTP
// Incluye el tipo de contenido y el token de autenticación si existe en localStorage
const getHeaders = () => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('token');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

// --- ADAPTERS (Adaptadores de Datos) ---
// Estos adaptadores transforman los datos entre el formato del Backend (snake_case)
// y el formato del Frontend (camelCase) para mantener consistencia en cada lado.

// Transforma los datos recibidos del Backend al formato del Frontend
const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('T')) return new Date(dateStr);
    return new Date(dateStr.replace(' ', 'T') + 'Z');
};

const toFrontend = (backendData) => {
    return {
        id: backendData.id,
        planta: backendData.planta,
        operacion: backendData.operacion,
        proceso: backendData.proceso,
        cliente: backendData.cliente,
        destino: backendData.destino,
        tipoVehiculo: backendData.tipo_vehiculo,
        numeroCarga: backendData.numero_carga,
        placa: backendData.placa,
        transportadora: backendData.transportadora,
        conductor: backendData.conductor,
        documentoConductor: backendData.documento_conductor,
        // Formatea la fecha para eliminar la parte de tiempo si existe
        fechaProgramacion: backendData.fecha_programacion ? backendData.fecha_programacion.split('T')[0].split(' ')[0] : '',
        pesoProgramado: backendData.peso_programado,
        observaciones: backendData.observaciones,
        status: backendData.status,
        muelle: backendData.muelle,
        isActive: Boolean(backendData.is_active),
        causalRechazo: backendData.causal_rechazo,
        location: backendData.location,
        arl: backendData.arl,
        vencimientoArl: backendData.vencimiento_arl ? backendData.vencimiento_arl.split('T')[0] : '',
        estadoArl: backendData.estado_arl,
        createdAt: backendData.created_at,
        // Agrupa todos los timestamps de hitos logísticos en un objeto
        timestamps: {
            llegadaParqueadero: parseDate(backendData.ts_llegada_parqueadero),
            ingresoPlanta: parseDate(backendData.ts_ingreso_planta),
            ingresoBascula: parseDate(backendData.ts_ingreso_bascula),
            asignacionMuelle: parseDate(backendData.ts_asignacion_muelle),
            inicioOperacion: parseDate(backendData.ts_inicio_operacion),
            finOperacion: parseDate(backendData.ts_fin_operacion),
            inicioDespacho: parseDate(backendData.ts_inicio_despacho),
            salida: parseDate(backendData.ts_salida),
            rechazo: parseDate(backendData.ts_rechazo),
        }
    };
};

// Helper para validar fecha
export const isValidDate = (year, month, day) => {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1000 || y > 9999) return false;
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === (m - 1) && date.getDate() === d;
};

// Normalizador universal de fechas a YYYY-MM-DD
export const normalizeToYYYYMMDD = (val) => {
    if (val === null || val === undefined) return null;

    if (val instanceof Date) {
        if (isNaN(val.getTime())) return null;
        const year = val.getFullYear();
        const month = val.getMonth() + 1;
        const day = val.getDate();
        if (!isValidDate(year, month, day)) return null;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    if (typeof val === 'number' || (typeof val === 'string' && /^\d+(\.\d+)?$/.test(String(val).trim()))) {
        const num = parseFloat(val);
        if (!isNaN(num) && num > 1000) {
            const date = new Date(Math.round((num - 25569) * 86400 * 1000));
            if (!isNaN(date.getTime())) {
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth() + 1;
                const day = date.getUTCDate();
                if (isValidDate(year, month, day)) {
                    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                }
            }
        }
    }

    if (typeof val === 'string') {
        const trimmed = val.trim();
        if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') return null;

        // Formato Latino / Español: DD/MM/YYYY o DD-MM-YYYY
        const latinoMatch = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (latinoMatch) {
            const day = parseInt(latinoMatch[1], 10);
            const month = parseInt(latinoMatch[2], 10);
            const year = parseInt(latinoMatch[3], 10);
            if (isValidDate(year, month, day)) {
                return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            }
            return null;
        }

        // Formato Estándar: YYYY-MM-DD o YYYY/MM/DD (con o sin hora T/espacio)
        const stdMatch = trimmed.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})(?:[T\s].*)?$/);
        if (stdMatch) {
            const year = parseInt(stdMatch[1], 10);
            const month = parseInt(stdMatch[2], 10);
            const day = parseInt(stdMatch[3], 10);
            if (isValidDate(year, month, day)) {
                return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            }
            return null;
        }

        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = parsed.getMonth() + 1;
            const day = parsed.getDate();
            if (isValidDate(year, month, day)) {
                return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            }
        }
        return null;
    }

    return null;
};

// Helper para sanitizar valores de fecha
export const sanitizeDate = (val) => {
    return normalizeToYYYYMMDD(val);
};

// Transforma los datos del Frontend al formato esperado por el Backend
const toBackend = (frontendData) => {
    const data = {
        planta: frontendData.planta,
        operacion: frontendData.operacion,
        proceso: frontendData.proceso,
        cliente: frontendData.cliente,
        destino: frontendData.destino,
        tipo_vehiculo: frontendData.tipoVehiculo,
        numero_carga: frontendData.numeroCarga,
        placa: frontendData.placa,
        transportadora: frontendData.transportadora,
        conductor: frontendData.conductor,
        documento_conductor: frontendData.documentoConductor,
        fecha_programacion: normalizeToYYYYMMDD(frontendData.fechaProgramacion),
        peso_programado: frontendData.pesoProgramado,
        observaciones: frontendData.observaciones,
        status: frontendData.status,
        muelle: frontendData.muelle,
        is_active: frontendData.isActive,
        causal_rechazo: frontendData.causalRechazo,
        location: frontendData.location,
        arl: frontendData.arl,
        vencimiento_arl: normalizeToYYYYMMDD(frontendData.vencimientoArl),
        estado_arl: frontendData.estadoArl,
    };

    // Aplana (flatten) el objeto timestamps para que coincida con las columnas de la BD
    if (frontendData.timestamps) {
        if (frontendData.timestamps.llegadaParqueadero !== undefined) data.ts_llegada_parqueadero = frontendData.timestamps.llegadaParqueadero;
        if (frontendData.timestamps.ingresoPlanta !== undefined) data.ts_ingreso_planta = frontendData.timestamps.ingresoPlanta;
        if (frontendData.timestamps.ingresoBascula !== undefined) data.ts_ingreso_bascula = frontendData.timestamps.ingresoBascula;
        if (frontendData.timestamps.asignacionMuelle !== undefined) data.ts_asignacion_muelle = frontendData.timestamps.asignacionMuelle;
        if (frontendData.timestamps.inicioOperacion !== undefined) data.ts_inicio_operacion = frontendData.timestamps.inicioOperacion;
        if (frontendData.timestamps.finOperacion !== undefined) data.ts_fin_operacion = frontendData.timestamps.finOperacion;
        if (frontendData.timestamps.inicioDespacho !== undefined) data.ts_inicio_despacho = frontendData.timestamps.inicioDespacho;
        if (frontendData.timestamps.salida !== undefined) data.ts_salida = frontendData.timestamps.salida;
        if (frontendData.timestamps.rechazo !== undefined) data.ts_rechazo = frontendData.timestamps.rechazo;
    }

    return data;
};

// --- API FUNCTIONS (Funciones de Interacción con la API) ---

// Maneja los errores de respuesta de la API de forma unificada
// Intenta extraer el mensaje de error más específico posible (JSON o texto)
const handleApiError = async (response) => {
    let errorMessage = `Error ${response.status}`;

    if (response.status === 401) {
        auth.logout();
        window.location.reload();
        throw new Error("Sesión expirada o no autorizada. Redirigiendo...");
    }

    try {
        const errorText = await response.text();
        try {
            const errorJson = JSON.parse(errorText);
            // Priorizamos mensaje limpio del backend
            errorMessage = errorJson.message || errorJson.error || JSON.stringify(errorJson);
        } catch (e) {
            // Si no es JSON, usamos el texto plano
            if (errorText && errorText.trim().length > 0) {
                errorMessage = errorText;
            } else {
                errorMessage = response.statusText || errorMessage;
            }
        }
    } catch (e) {
        errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
};

// Obtiene la lista de todos los vehículos
export const fetchVehicles = async () => {
    const response = await fetch(`${API_URL}/vehicles`, {
        method: 'GET',
        headers: getHeaders(),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    const data = await response.json();
    // Convierte cada vehículo al formato del frontend
    return data.map(toFrontend);
};

// Crea un nuevo registro de vehículo
export const createVehicle = async (vehicleData) => {
    const backendData = toBackend(vehicleData);
    const response = await fetch(`${API_URL}/vehicles`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(backendData),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    const result = await response.json();
    return toFrontend(result);
};

// Actualiza un vehículo existente
export const updateVehicle = async (id, updates) => {
    // El frontend a veces envía actualizaciones con claves planas como 'timestamps.llegadaParqueadero'
    // Aquí mapeamos esas claves a las columnas correspondientes del backend
    const backendUpdates = {};

    Object.keys(updates).forEach(key => {
        if (key.startsWith('timestamps.')) {
            const tsKey = key.split('.')[1];
            // Mapeo de claves de timestamp a columnas snake_case
            const map = {
                llegadaParqueadero: 'ts_llegada_parqueadero',
                ingresoPlanta: 'ts_ingreso_planta',
                ingresoBascula: 'ts_ingreso_bascula',
                asignacionMuelle: 'ts_asignacion_muelle',
                inicioOperacion: 'ts_inicio_operacion',
                finOperacion: 'ts_fin_operacion',
                inicioDespacho: 'ts_inicio_despacho',
                salida: 'ts_salida',
                rechazo: 'ts_rechazo'
            };
            if (map[tsKey]) {
                backendUpdates[map[tsKey]] = updates[key];
            }
        } else {
            // Mapeo de campos estándar
             const fieldMap = {
                tipoVehiculo: 'tipo_vehiculo',
                numeroCarga: 'numero_carga',
                documentoConductor: 'documento_conductor',
                fechaProgramacion: 'fecha_programacion',
                pesoProgramado: 'peso_programado',
                isActive: 'is_active',
                causalRechazo: 'causal_rechazo',
                // Campos con identidad directa
                planta: 'planta',
                operacion: 'operacion',
                proceso: 'proceso',
                cliente: 'cliente',
                destino: 'destino',
                placa: 'placa',
                transportadora: 'transportadora',
                conductor: 'conductor',
                observaciones: 'observaciones',
                status: 'status',
                muelle: 'muelle',
                location: 'location',
                arl: 'arl',
                vencimientoArl: 'vencimiento_arl',
                estadoArl: 'estado_arl'
            };
            if (fieldMap[key]) {
                let val = updates[key];
                if (key === 'vencimientoArl' || key === 'fechaProgramacion') {
                    val = sanitizeDate(val);
                }
                backendUpdates[fieldMap[key]] = val;
            }
        }
    });

    const response = await fetch(`${API_URL}/vehicles/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(backendUpdates),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    const result = await response.json();
    return toFrontend(result);
};

// Elimina (lógicamente) un vehículo
export const deleteVehicle = async (id) => {
    const response = await fetch(`${API_URL}/vehicles/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    return response.json();
};

// --- ENTREGAS API ---

export const fetchEntregas = async () => {
    const response = await fetch(`${API_URL}/entregas`, {
        method: 'GET',
        headers: getHeaders(),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    return response.json();
};

export const fetchVehiculoPorCarga = async (numeroCarga) => {
    const response = await fetch(`${API_URL}/entregas/vehiculo/${numeroCarga}`, {
        method: 'GET',
        headers: getHeaders(),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    return response.json();
};

export const createEntrega = async (entregaData) => {
    const response = await fetch(`${API_URL}/entregas`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(entregaData),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    return response.json();
};

export const updateEntrega = async (id, entregaData) => {
    const response = await fetch(`${API_URL}/entregas/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(entregaData),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    return response.json();
};

export const deleteEntrega = async (id) => {
    const response = await fetch(`${API_URL}/entregas/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
    });
    if (!response.ok) {
        await handleApiError(response);
    }
    return response.json();
};
